// frontend/modules/loan-reminder/hooks/useTemplateOptions.ts
import { useEffect, useState, useMemo } from 'react';
import { NotificationPlatforms, TextTemplateDTO } from '@/shared/api/model';

// Import the API function that would be generated by Orval
import { useGetAllTextTemplates } from '@/shared/api/client/text-template-controller';

interface TemplateOption {
    name: string;
    displayName: string;
    platform: NotificationPlatforms;
}

export const useTemplateOptions = (platform?: NotificationPlatforms) => {
    const [templateOptions, setTemplateOptions] = useState<TemplateOption[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Get templates from API
    const {
        data: templatesData,
        isLoading: isLoadingTemplates,
        error: templatesError
    } = useGetAllTextTemplates();

    // Process templates into options
    useEffect(() => {
        if (templatesData) {
            const templates = templatesData;

            // Group templates by name and platform
            const templateMap = new Map<string, TemplateOption>();

            templates.forEach((template: TextTemplateDTO) => {
                const key = `${template.name}_${template.platform}`;

                if (!templateMap.has(key)) {
                    templateMap.set(key, {
                        name: template.name || '',
                        displayName: formatTemplateDisplayName(template.name || ''),
                        platform: template.platform as NotificationPlatforms
                    });
                }
            });

            setTemplateOptions(Array.from(templateMap.values()));
            setError(null);
        } else {
            setTemplateOptions([]);
        }
    }, [templatesData]);

    // Handle error state
    useEffect(() => {
        if (templatesError) {
            console.error('Error loading templates:', templatesError);
            setError('Failed to load message templates');
        }
    }, [templatesError]);

    // Update loading state
    useEffect(() => {
        setIsLoading(isLoadingTemplates);
    }, [isLoadingTemplates]);

    // Filter options by platform if specified
    const filteredOptions = useMemo(() => {
        if (!platform) {
            return templateOptions;
        }
        return templateOptions.filter(option => option.platform === platform);
    }, [templateOptions, platform]);

    return {
        templateOptions: filteredOptions,
        isLoading,
        error
    };
};

// Helper function to format template names for display
const formatTemplateDisplayName = (name: string): string => {
    if (!name) return '';

    // Convert snake_case to Title Case with spaces
    return name
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
};